import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface MainSectionCardProps {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
  defaultOpen?: boolean;
  className?: string;
  metrics?: {
    total: number;
    change?: number;
    changeType?: 'increase' | 'decrease' | 'neutral';
    unit?: string;
  };
  color?: 'blue' | 'green' | 'purple' | 'orange' | 'red' | 'indigo';
  titleColor?: string;
}

const getColorValue = (color: string): string => {
  const colorMap: Record<string, string> = {
    blue: '#3b82f6',
    green: '#10b981',
    purple: '#a855f7',
    orange: '#f97316',
    red: '#ef4444',
    indigo: '#6366f1',
    emerald: '#10b981',
    cyan: '#06b6d4',
    pink: '#ec4899'
  };
  return colorMap[color] || color;
};

const MainSectionCard: React.FC<MainSectionCardProps> = ({
  title,
  icon: Icon,
  children,
  defaultOpen = false,
  metrics,
  titleColor
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const formatNumber = (value: number) => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
    return value.toString();
  };

  const getChangeIcon = () => {
    if (!metrics || !metrics.changeType) return null;
    switch (metrics.changeType) {
      case 'increase':
        return <TrendingUp className="h-4 w-4" style={{ color: 'var(--text-secondary)' }} />;
      case 'decrease':
        return <TrendingDown className="h-4 w-4" style={{ color: 'var(--text-secondary)' }} />;
      default:
        return <Minus className="h-4 w-4" style={{ color: 'var(--text-secondary)' }} />;
    }
  };


  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="border-2 rounded-2xl shadow-lg overflow-hidden"
      style={{
        backgroundColor: 'var(--bg-card)',
        borderColor: 'var(--border-primary)',
        boxShadow: 'var(--shadow-lg)'
      }}
    >
      {/* Header */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-5 flex items-center justify-between transition-all duration-200 group"
        style={{
          backgroundColor: 'transparent'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'var(--bg-card-hover)'
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent'
        }}
      >
        <div className="flex items-center space-x-4">
          <div 
            className="p-3 rounded-xl shadow-sm group-hover:scale-105 transition-transform duration-200"
            style={{
              backgroundColor: 'var(--bg-tertiary)',
              opacity: 0.8
            }}
          >
            <div style={{ color: 'var(--primary)' }}>
              <Icon className="h-6 w-6" />
            </div>
          </div>
          <div className="text-left">
            <h2
              className="text-xl font-bold bg-gradient-to-r bg-clip-text text-transparent"
              style={{
                backgroundImage: titleColor
                  ? `linear-gradient(135deg, ${getColorValue(titleColor)} 0%, ${getColorValue(titleColor)}dd 100%)`
                  : undefined,
                color: titleColor ? undefined : 'var(--text-primary)'
              }}
            >
              {title}
            </h2>
            {metrics && (
              <div className="flex items-center space-x-2 mt-1">
                <span 
                  className="text-2xl font-bold"
                  style={{ color: 'var(--text-primary)' }}
                >
                  {formatNumber(metrics.total)}
                </span>
                {metrics.unit && (
                  <span
                    className="text-sm"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    {metrics.unit}
                  </span>
                )}
                {metrics.change !== undefined && (
                  <div
                    className="flex items-center space-x-1"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    {getChangeIcon()}
                    <span className="text-sm font-medium">
                      {metrics.change > 0 ? '+' : ''}{metrics.change}%
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3 }}
          className="p-2 rounded-lg transition-colors duration-200"
          style={{ 
            color: 'var(--text-secondary)',
            backgroundColor: 'transparent'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--bg-card-hover)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent'
          }}
        >
          <ChevronDown className="h-5 w-5" style={{ color: 'var(--text-secondary)' }} />
        </motion.div>
      </button>
      
      {/* Content */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-6 pt-2">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default MainSectionCard;
